import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { useTooltipHover } from '../../hooks/use_tooltip_hover.js';
import { getSpacing } from '../../styles/spacing.js';
import { mergeProps } from '../../utils/react.js';
import { Box } from '../Box/Box.js';
import { Popper } from '../Popper/Popper.js';
import { TooltipArrow } from './TooltipArrow.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["arrow", "children", "containerSelector", "content", "id", "inline", "interactive", "interactiveDelay", "onHide", "onShow", "position", "showing", "size", "variant"];
var DEFAULT_MAX_WIDTH = 200;
var TooltipContent = styled(Box).withConfig({
  displayName: "Tooltip__TooltipContent",
  componentId: "ttt6x9-0"
})(["&&{", ";", ";font-size:14px;line-height:20px;min-height:", ";max-height:inherit;overflow:auto;overflow-wrap:break-word;}"], function (props) {
  return props.size === "sm" && "\n        padding: ".concat(getSpacing(1), " ").concat(getSpacing(2), ";\n    ");
}, function (props) {
  return props.size === "lg" && "\n        padding: ".concat(getSpacing(2), " ").concat(getSpacing(4), ";\n    ");
}, getSpacing(7));

var Tooltip = function Tooltip(props) {
  var _useId = useId(1, "tooltip-"),
      _useId2 = _slicedToArray(_useId, 1),
      defaultId = _useId2[0];

  var _props$arrow = props.arrow,
      arrow = _props$arrow === void 0 ? true : _props$arrow,
      children = props.children,
      containerSelector = props.containerSelector,
      content = props.content,
      _props$id = props.id,
      id = _props$id === void 0 ? defaultId : _props$id,
      inline = props.inline,
      interactive = props.interactive,
      interactiveDelay = props.interactiveDelay,
      onHide = props.onHide,
      onShow = props.onShow,
      _props$position = props.position,
      position = _props$position === void 0 ? "top" : _props$position,
      showing = props.showing,
      _props$size = props.size,
      size = _props$size === void 0 ? "sm" : _props$size,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? "dark" : _props$variant,
      rest = _objectWithoutProperties(props, _excluded);

  var _useTooltipHover = useTooltipHover({
    open: showing,
    interactive: interactive,
    interactiveDelay: interactiveDelay,
    onShow: onShow,
    onHide: onHide
  }),
      showingState = _useTooltipHover.open,
      bindTrigger = _useTooltipHover.bindTrigger,
      bindTooltip = _useTooltipHover.bindTooltip;

  if (!content) return children;
  return /*#__PURE__*/jsx(Popper, {
    inline: inline,
    containerSelector: containerSelector,
    showing: showingState,
    unrestrictedHeight: true,
    offset: [0, 11],
    placement: position,
    renderArrow: arrow ? function (_ref) {
      var arrowPosition = _ref.position;
      return /*#__PURE__*/jsx(TooltipArrow, {
        variant: variant,
        position: arrowPosition,
        size: size
      });
    } : void 0,
    renderContent: function renderContent(_ref2) {
      var ref = _ref2.ref,
          style = _ref2.style;
      return /*#__PURE__*/jsx(TooltipContent, _objectSpread(_objectSpread({}, mergeProps({
        style: {
          maxWidth: DEFAULT_MAX_WIDTH
        }
      }, rest, _objectSpread(_objectSpread({}, bindTooltip), {}, {
        borderRadius: 1,
        elevation: 1,
        id: id,
        ref: ref,
        role: "tooltip",
        size: size,
        style: style,
        variant: variant
      }))), {}, {
        children: content
      }));
    },
    renderTrigger: function renderTrigger(_ref3) {
      var ref = _ref3.ref;
      return /*#__PURE__*/React.cloneElement(children, mergeProps(_objectSpread(_objectSpread({}, children.props), {}, {
        ref: children.ref
      }), _objectSpread(_objectSpread({}, bindTrigger), {}, {
        "aria-describedby": showing ? id : void 0,
        ref: ref
      })));
    }
  });
};

Tooltip.propTypes = {
  "arrow": _PropTypes.bool,
  "children": _PropTypes.element,
  "containerSelector": _PropTypes.string,
  "content": _PropTypes.node,
  "id": _PropTypes.string,
  "inline": _PropTypes.bool,
  "interactive": _PropTypes.bool,
  "interactiveDelay": _PropTypes.number,
  "onHide": _PropTypes.func,
  "onShow": _PropTypes.func,
  "position": _PropTypes.oneOf(["bottom", "left", "right", "top"]),
  "showing": _PropTypes.bool,
  "size": _PropTypes.oneOf(["lg", "sm"]),
  "variant": _PropTypes.oneOf(["dark", "light"])
};
Tooltip.displayName = "Tooltip";

export { Tooltip };
